import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ESOganisationDropdown from "../components/dropdown/esOrganisationDropdown";
import SectionHeader from "../components/section/SectionHeader";
import { DFSI_GROUPS_TABLE_HEADERS } from "../constants";
import AuthContext from "../store/authContext";
import {
  errorAlert,
  makeApiRequest,
  successAlert,
  transformDFSIGroupResponseRow,
  transformDFSIGroupUpdateBody,
  transformDFSIGroupsRows,
} from "../util";
import DataTableWithControls from "../components/dataTable/DataTableWithControls";
import OrgContext from "../store/orgContext";
import InfoAlert from "../ui/InfoAlert";
import TableActionButton from "../ui/TableActionButton";
import {
  deleteDFSIGroup,
  getDFSIGroupByRef,
  updateDFSIGroupByRef,
} from "../api";
import useHideSidePanel from "../hooks/useHideSidePanel";
import EditDFSIGroupRow from "../components/dataTable/EditDFSIGroupRow";
import { INITIAL_DFSI_GROUP_EDIT_STATE } from "../constants/editState";
import {
  BULK_UPLOAD_GENERAL_TRANSLATIONS,
  DELETE_DFSI_GROUP_TRANSLATIONS,
  READ_DFSI_GROUP_TRANSLATIONS,
} from "../translations/util";
import useAppTitle from "../hooks/useAppTitle";
import { EDIT_DFSI_GROUP_FORM_FIELDS } from "../constants/editFormFields";
import SearchContext from "../store/searchContext";
import BulkUpload from "../components/bulkUpload/BulkUpload";
import DFSIContext from "../store/dfsiContext";

interface ISearchData {
  "CONNECTION-ID"?: string | null;
}

const getAlertHeading = ({
  dfsiChannelIDExists,
  groupData,
}: {
  dfsiChannelIDExists: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  groupData: Array<any>;
}) => {
  if (!dfsiChannelIDExists) {
    return READ_DFSI_GROUP_TRANSLATIONS.infoAlertHeading.dfsiChannelIDExists;
  }

  if (!groupData) {
    return READ_DFSI_GROUP_TRANSLATIONS.infoAlertHeading.groupData;
  }

  return undefined;
};

const getAlertSubText = ({
  dfsiChannelIDExists,
  groupData,
}: {
  dfsiChannelIDExists: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  groupData: Array<any>;
}) => {
  if (!dfsiChannelIDExists) {
    return READ_DFSI_GROUP_TRANSLATIONS.infoAlertSubText.dfsiChannelIDExists;
  }

  if (!groupData) {
    return READ_DFSI_GROUP_TRANSLATIONS.infoAlertSubText.groupData;
  }

  return undefined;
};

const DFSIGroups = () => {
  const {
    token,
    scopes: { dfsi_groups: dfsiGroups },
  } = useContext(AuthContext);
  const { orgs: orgList, selectedOrg, updateOrg } = useContext(OrgContext);
  const { setDfsiChannelData } = useContext(DFSIContext);
  const { selectedColumn, setSelectedColumn, setSearchText, setDisableSearch } =
    useContext(SearchContext);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [connectionsData, setConnectionsData] = useState<any[]>([]);

  // State to check if Bulk Upload is Clicked
  const [isBulkUploadEnabled, setIsBulkUploadEnabled] = useState(false);
  const { showSidePanel, handleToggleSidePanel } = useHideSidePanel();
  const [selectedConnectionId, setSelectedConnectionId] = useState<
    string | null
  >(null);
  const [numberOfRows, setNumberOfRows] = useState(0);
  const [numberOfPages, setNumberOfPages] = useState(0);
  const [searchQuery, setSearhQuery] = useState<{
    data: ISearchData;
    pageNum: number;
    pageSize: number;
    sortByField?: string;
    sortDirection?: "desc" | "asc";
  }>({
    pageSize: 20,
    pageNum: 1,
    data: {},
    sortByField: undefined,
    sortDirection: undefined,
  });
  useAppTitle("DFSI Groups- IWF Config Admin Tool (ICAT) by AT&T");

  const [dfsiChannelIDExists, setdfsiChannelIDExists] = useState(true);

  // Instructions to display in DFSI-Group bulk upload pannel
  const DFSI_GROUP_NOTES = [
    BULK_UPLOAD_GENERAL_TRANSLATIONS.Notes.point1,
    BULK_UPLOAD_GENERAL_TRANSLATIONS.Notes.point2,
    BULK_UPLOAD_GENERAL_TRANSLATIONS.Notes.hexValueNote,
  ];

  // DFSI Channel is required to create DFSI Group, hence this function will check if DFSI Channel Ids exists for the selected Org-ID.
  const getDFSIChannelIds = async (orgId: string) => {
    try {
      const response = await makeApiRequest<{
        Rows: { "CHANNEL-ID": string }[];
      }>({
        path: `/api/v1/app-config/dfsi-channels/ids/`,
        token,
        org: orgId,
      });
      setdfsiChannelIDExists(response.Rows.length > 0);
      setDfsiChannelData(response.Rows.map((row) => row["CHANNEL-ID"]));
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    }
  };
  const fetchDFSIGroups = async () => {
    setLoading(true);
    try {
      if (selectedOrg) {
        const { data, pageNum, pageSize, sortByField, sortDirection } =
          searchQuery;
        const queryParams = new URLSearchParams({
          page: `${pageNum}`,
          pageSize: `${pageSize}`,
          ...(sortByField && { sortByField }),
          ...(sortDirection && { sortDirection }),
          ...(data &&
            data["CONNECTION-ID"] && {
              "CONNECTION-ID": data["CONNECTION-ID"],
            }),
        });
        const response = await makeApiRequest<{
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          Rows: Array<any>;
          NbrOfRows: number;
          NbrOfPages: number;
        }>({
          path: `/api/v1/app-config/dfsi-groups?${queryParams}`,
          token,
          org: selectedOrg.id,
        });
        setNumberOfRows(response.NbrOfRows);
        setNumberOfPages(response.NbrOfPages);
        setConnectionsData(response.Rows);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  const deleteConnection = async (ref: string) => {
    if (selectedOrg && token) {
      try {
        await deleteDFSIGroup({
          token,
          ref,
          org: selectedOrg.id,
        });
        successAlert(DELETE_DFSI_GROUP_TRANSLATIONS.successAlertMessage);
        handleToggleSidePanel();
        setSelectedConnectionId(null);
        fetchDFSIGroups();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        errorAlert(e);
      }
    }
  };

  const sortTable = (col: string, direction: "asc" | "desc") => () => {
    setSearhQuery({
      ...searchQuery,
      sortByField: col,
      sortDirection: direction,
    });
  };

  useEffect(() => {
    setSelectedColumn(null);
    setSearchText("");
    setDisableSearch(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSearhQuery({
      pageSize: 20,
      pageNum: 1,
      data: {},
      sortByField: undefined,
      sortDirection: undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg]);

  useEffect(() => {
    fetchDFSIGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg, searchQuery]);

  useEffect(() => {
    if (selectedOrg) {
      getDFSIChannelIds(selectedOrg.id);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg]);

  return (
    <>
      <SectionHeader
        pageTitle={READ_DFSI_GROUP_TRANSLATIONS.header}
        pageDescription={READ_DFSI_GROUP_TRANSLATIONS.headerSubtext}
        sideComponent={
          <div className="dropdown-details">
            <ESOganisationDropdown
              org={selectedOrg || undefined}
              orgsList={orgList}
              setOrg={updateOrg}
            />
          </div>
        }
      />

      {showSidePanel && isBulkUploadEnabled ? (
        <BulkUpload
          moduleName="DFSI Group"
          pathName="/api/v1/app-config/dfsi-groups"
          csvTemplateFile={[
            "ENCRYPTION-ENABLED,STORAGE-LOCATION-NUMBER,FORMAT,DFSI-LOGICAL-GID,CHANNEL-IDS,DFSI-GROUP-NAME",
          ]}
          closeBulkUploadSidePanel={() => {
            handleToggleSidePanel();
            setIsBulkUploadEnabled(false);
            fetchDFSIGroups();
          }}
          notes={DFSI_GROUP_NOTES}
        />
      ) : null}

      {selectedOrg ? (
        <>
          {showSidePanel &&
          selectedOrg &&
          selectedConnectionId &&
          !isBulkUploadEnabled ? (
            <EditDFSIGroupRow
              panelName={READ_DFSI_GROUP_TRANSLATIONS.editRow.panelName}
              initialState={INITIAL_DFSI_GROUP_EDIT_STATE}
              org={selectedOrg.id}
              esRef={selectedConnectionId}
              setShowEditPanel={handleToggleSidePanel}
              onDelete={() => deleteConnection(selectedConnectionId)}
              header={READ_DFSI_GROUP_TRANSLATIONS.editRow.header}
              transformResponseRow={transformDFSIGroupResponseRow}
              transformUpdateBody={transformDFSIGroupUpdateBody}
              onUpdate={updateDFSIGroupByRef}
              loadDataByRef={getDFSIGroupByRef}
              hasDeletePermission={dfsiGroups && dfsiGroups.includes("d")}
              hasEditPermission={dfsiGroups && dfsiGroups.includes("u")}
              refreshTable={fetchDFSIGroups}
              deleteModalItemName="DFSI-LOGICAL-GID"
              formFields={EDIT_DFSI_GROUP_FORM_FIELDS}
            />
          ) : null}

          <DataTableWithControls
            loading={loading}
            showAdvancdSearch={false}
            showSearch={false}
            filterColumns={[]}
            selectedColumn={selectedColumn}
            setSelectedColumn={setSelectedColumn}
            onSearch={() => null}
            showCreateButton
            onCreateButtonClick={() => {
              navigate(`/configurationManagement/dfsiGroups/new`);
            }}
            rows={transformDFSIGroupsRows(connectionsData).map(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (row: any) => ({
                ...row,
                action: (
                  <TableActionButton
                    onActionButtonClick={() => {
                      setSelectedConnectionId(row.id);
                      handleToggleSidePanel();
                    }}
                  />
                ),
              })
            )}
            headers={DFSI_GROUPS_TABLE_HEADERS}
            pageNum={searchQuery.pageNum}
            setPageNum={(pageNum) => setSearhQuery({ ...searchQuery, pageNum })}
            totalItems={numberOfRows}
            totalPages={numberOfPages}
            pageSize={searchQuery.pageSize}
            setPageSize={(pageSize) =>
              setSearhQuery({ ...searchQuery, pageSize })
            }
            sortByField={searchQuery.sortByField}
            sortDirection={searchQuery.sortDirection}
            handleSort={sortTable}
            infoAlertHeading={getAlertHeading({
              dfsiChannelIDExists,
              groupData: connectionsData,
            })}
            infoAlertSubtext={getAlertSubText({
              dfsiChannelIDExists,
              groupData: connectionsData,
            })}
            showBulkUploadButton={dfsiGroups && dfsiGroups.includes("c")}
            isDFSIdataExists={!dfsiChannelIDExists}
            onBulkUploadButtonClick={() => {
              handleToggleSidePanel();
              setIsBulkUploadEnabled(true);
            }}
          />
        </>
      ) : (
        <InfoAlert
          heading={READ_DFSI_GROUP_TRANSLATIONS.infoAlert.heading}
          subText={READ_DFSI_GROUP_TRANSLATIONS.infoAlert.subText}
        />
      )}
    </>
  );
};

export default DFSIGroups;
